import { action, computed, makeObservable, observable } from 'mobx';
import type { ApplicationSdk } from '../../core/sdk';

export interface FooterButtonConfig {
    label: string;
    icon?: string;
    onClick?: () => null | void | Promise<void>;
    style?: React.CSSProperties;
    className?: string;
}

export interface BackButtonConfig {
    visible?: boolean;
    disabled?: boolean;
    customAction?: () => undefined | Promise<void>;
}
export interface MenuHeaderConfig {
    title?: string;
    icon?: string;
}
export interface MenuConfig {
    showBackButton?: boolean;
    backButton?: BackButtonConfig;
    customHeader?: MenuHeaderConfig;
}

/**
 * Abstract base class for employee stores with common functionality
 * Provides menu control and footer button management for employee features
 */
abstract class BaseEmployeeStore {
    protected sdk: ApplicationSdk;

    public isLoading: boolean = false;

    public footerButton: FooterButtonConfig = {
        label: 'Start',
        icon: 'pi pi-home',
        onClick: () => null,
    };

    public menuConfig: MenuConfig = {
        showBackButton: true,
        backButton: {
            visible: true,
            disabled: false,
            customAction: undefined,
        },
        customHeader: undefined,
    };

    constructor(sdk: ApplicationSdk) {
        this.sdk = sdk;

        makeObservable(this, {
            isLoading: observable,
            menuConfig: observable,
            footerButton: observable,
            setLoading: action,
            setMenuConfig: action,
            updateFooterButton: action,
            isFooterButtonVisible: computed,
            isFooterButtonEnabled: computed,
            footerButtonHelpText: computed,
            isFooterButtonLoading: computed,
            isBackButtonVisible: computed,
            isBackButtonDisabled: computed,
            isHeaderHidden: computed,
        });
    }

    public async init(): Promise<void> {
        this.onInit();
    }

    public setLoading(loading: boolean): void {
        this.isLoading = loading;
    }

    /**
     * Update menu configuration
     */
    public setMenuConfig(config: Partial<MenuConfig>): void {
        this.menuConfig = { ...this.menuConfig, ...config };
    }

    public get isHeaderHidden(): boolean {
        return false;
    }

    public get isBackButtonVisible(): boolean {
        return false;
    }

    /**
     * Check if back button should be disabled
     */
    public get isBackButtonDisabled(): boolean {
        return false;
    }

    /**
     * Navigate back with safety checks
     */
    public async navigateBack(): Promise<void> {
        // Check for custom back action first
        if (this.menuConfig.backButton?.customAction) {
            return this.menuConfig.backButton.customAction();
        }

        // Default back behavior
        if (await this.onBeforeNavigateBack()) {
            this.sdk.routeTo('/employee/menu');
        }
    }

    /**
     * Called before navigating back - return false to prevent navigation
     */
    protected async onBeforeNavigateBack(): Promise<boolean> {
        return true;
    }

    public updateFooterButton(config: Partial<FooterButtonConfig>): void {
        this.footerButton = { ...this.footerButton, ...config };
    }

    get isFooterButtonVisible(): boolean {
        return true;
    }

    get isFooterButtonEnabled(): boolean {
        return false;
    }

    get isFooterButtonLoading(): boolean {
        return false;
    }

    get footerButtonHelpText(): string {
        return '';
    }

    /**
     * Abstract method - implement initialization logic in subclasses
     */
    protected abstract onInit(): Promise<void>;

    protected showSuccess(message: string): void {
        this.sdk.showSuccessToast(message);
    }

    protected showError(message: string): void {
        this.sdk.showErrorToast(message);
    }
}

export default BaseEmployeeStore;
